import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';

import { ScryfallCard } from '@/types/scryfall';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { Check, Copy, Loader2 } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '@/lib/utils';
import { Deck, Card, isFormatTag } from '@/lib/nostr/deck';
import { useScryfall } from '@/hooks/useScryfall';

interface ShareListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deck: Deck | null;
  deckUrl?: string;
}

const generateDecklist = (cards: Card[], commanderNames: string[] = []) => {
  let mainDeckText = '';
  if (commanderNames.length > 0) {
    mainDeckText += commanderNames.map(name => `1 ${name} *CMDR*`).join('\n') + '\n';
  }
  mainDeckText += cards
    .filter(c => !commanderNames.includes(c.name))
    .map(c => `${c.quantity} ${c.name}`)
    .sort()
    .join('\n');
  return mainDeckText;
};

const generateMtgoDecklist = (cards: (Card & { scryfallCard?: ScryfallCard })[], commanderNames: string[] = []) => {
  let mainDeckText = '';
  if (commanderNames.length > 0) {
    const commanderCards = cards.filter(c => commanderNames.includes(c.name));
    mainDeckText += commanderCards.map(commanderCard => {
      if (commanderCard && commanderCard.scryfallCard) {
        return `1 ${commanderCard.name} ${commanderCard.foil === 'foil' ? '(*F*) ' : ''}(${commanderCard.scryfallCard.set.toUpperCase()}) ${commanderCard.scryfallCard.collector_number} *CMDR*`;
      }
      return `1 ${commanderCard.name} *CMDR*`;
    }).join('\n') + '\n';
  }
  mainDeckText += cards
    .filter(c => !commanderNames.includes(c.name))
    .map(card => {
      if (card.scryfallCard) {
        return `${card.quantity} ${card.name} ${card.foil === 'foil' ? '(*F*) ' : ''}(${card.scryfallCard.set.toUpperCase()}) ${card.scryfallCard.collector_number}`;
      }
      return `${card.quantity} ${card.name}`;
    })
    .sort()
    .join('\n');
  return mainDeckText;
};

export const ShareListDialog = ({ open, onOpenChange, deck, deckUrl }: ShareListDialogProps) => {
  const { toast } = useToast();
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const scryfall = useScryfall();
  const [copied, setCopied] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [noteContent, setNoteContent] = useState('');
  const [scryfallCards, setScryfallCards] = useState<(Card & { scryfallCard?: ScryfallCard })[]>([]);
  const [isDataFetched, setIsDataFetched] = useState(false);

  useEffect(() => {
    if (deck && open && !isDataFetched) {
      const fetchScryfallData = async () => {
        const cardsToFetch: ({ name: string } | { set: string; collector_number: string })[] = [];
        const existingCards: (Card & { scryfallCard: ScryfallCard })[] = [];

        deck.cards.forEach(card => {
          if (card.scryfallCard) {
            existingCards.push(card as Card & { scryfallCard: ScryfallCard });
          } else {
            // Use set and collector_number (art_id) for precision
            if (card.set_id && card.art_id) {
              cardsToFetch.push({ set: card.set_id, collector_number: card.art_id });
            } else {
              cardsToFetch.push({ name: card.name });
            }
          }
        });

        if (cardsToFetch.length === 0) {
          setScryfallCards(existingCards);
          setIsDataFetched(true);
          return;
        }

        const batchSize = 75;
        const fetchedScryfallData: ScryfallCard[] = [];

        try {
          for (let i = 0; i < cardsToFetch.length; i += batchSize) {
            const batch = cardsToFetch.slice(i, i + batchSize);
            const results = await scryfall.getCardCollection(batch);
            fetchedScryfallData.push(...results);
          }

          const scryfallCardMap = new Map<string, ScryfallCard>();
          fetchedScryfallData.forEach(sc => {
            // Key by set and collector_number for an exact match
            if (sc.set && sc.collector_number) {
              scryfallCardMap.set(`${sc.set.toLowerCase()}-${sc.collector_number}`, sc);
            }
            // Fallback key by name for broader matching
            if (!scryfallCardMap.has(sc.name)) {
              scryfallCardMap.set(sc.name, sc);
            }
          });

          const newlyFetchedCards = deck.cards
            .filter(card => !card.scryfallCard)
            .map(card => {
              // Prioritize finding by set and collector_number
              const exactMatchKey = card.set_id && card.art_id ? `${card.set_id.toLowerCase()}-${card.art_id}` : null;
              let foundCard = exactMatchKey ? scryfallCardMap.get(exactMatchKey) : undefined;

              // Fallback to name if no exact match is found
              if (!foundCard) {
                foundCard = scryfallCardMap.get(card.name);
              }

              return { ...card, scryfallCard: foundCard };
            });

          setScryfallCards([...existingCards, ...newlyFetchedCards]);
        } catch (error) {
          console.error('Failed to fetch card collection', error);
          toast({
            title: 'Error Fetching Card Data',
            description: 'Could not retrieve all card details. Some information may be missing.',
            variant: 'destructive',
          });
          setScryfallCards(existingCards);
        } finally {
          setIsDataFetched(true);
        }
      };

      fetchScryfallData();
    } else if (!open && isDataFetched) {
      setIsDataFetched(false);
    }
  }, [deck, open, isDataFetched, scryfall, toast]);

  const decklistText = useMemo(() => deck ? generateDecklist(deck.cards, deck.commanders) : '', [deck]);
  const mtgoDecklistText = useMemo(() => generateMtgoDecklist(scryfallCards, deck?.commanders), [scryfallCards, deck]);
  const shareContent = useMemo(() => deckUrl || decklistText, [deckUrl, decklistText]);

  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyToClipboard = (text: string, type: 'decklist' | 'link' = 'decklist') => {
    navigator.clipboard.writeText(text);
    if (type === 'link') {
      setLinkCopied(true);
      toast({ title: 'Link copied to clipboard!' });
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      setCopied(true);
      toast({ title: 'Decklist copied to clipboard!' });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleImageSelection = (card: Card & { scryfallCard?: ScryfallCard }) => {
    if (!card.scryfallCard) return;
    const imageUrl = card.scryfallCard.image_uris?.art_crop || card.scryfallCard.image_uris?.large || card.scryfallCard.image_uris?.normal;
    if (!imageUrl) return;

    setSelectedImages(prev => {
      if (prev.includes(imageUrl)) {
        return prev.filter(url => url !== imageUrl);
      }
      if (prev.length < 4) {
        return [...prev, imageUrl];
      }
      toast({ title: 'You can select up to 4 images.', variant: 'destructive' });
      return prev;
    });
  };

  const handlePublishNote = () => {
    if (!deck) return;
    let content = noteContent || `Check out my ${deck.event.tags.find(isFormatTag)?.[1] || ''} deck, "${deck.title}"!`;
    content += `\n\n${shareContent}`;

    if (selectedImages.length > 0) {
      content += `\n\n${selectedImages.join('\n')}`;
    }

    publishEvent(
      { kind: 1, content },
      {
        onSuccess: () => {
          toast({ title: 'Note published successfully!' });
          onOpenChange(false);
        },
        onError: (err) => {
          toast({ title: 'Failed to publish note', description: err.message, variant: 'destructive' });
        },
      }
    );
  };

  if (!deck) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Share Decklist</DialogTitle>
          <DialogDescription>Share your deck with others or export it for other tools.</DialogDescription>
        </DialogHeader>
        {deckUrl && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Deck Link</label>
            <div className="flex gap-2">
              <Input value={deckUrl} readOnly className="font-mono text-sm" />
              <Button onClick={() => handleCopyToClipboard(deckUrl, 'link')} variant="outline" size="icon" className="shrink-0 aspect-square">
                {linkCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}
        <Tabs defaultValue="copy">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="copy">Copy Decklist</TabsTrigger>
            <TabsTrigger value="mtgo">MTGO Format</TabsTrigger>
            <TabsTrigger value="nostr">Share to Nostr</TabsTrigger>
          </TabsList>
          <TabsContent value="copy" className="py-4">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Copy the decklist in a format compatible with tools like Moxfield or TappedOut.
              </p>
              <Textarea value={decklistText} readOnly rows={10} className="font-mono text-sm" />
              <Button onClick={() => handleCopyToClipboard(decklistText, 'decklist')} className="w-full">
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="mtgo" className="py-4">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Copy the decklist in a format compatible with Magic: The Gathering Online.
              </p>
              <Textarea value={mtgoDecklistText} readOnly rows={10} className="font-mono text-sm" />
              <Button onClick={() => handleCopyToClipboard(mtgoDecklistText, 'decklist')} className="w-full">
                {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="nostr" className="py-4">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Note Content (optional)</label>
                <Textarea
                  placeholder={deckUrl ? `Check out my ${deck.event.tags.find(isFormatTag)?.[1] || ''} deck, "${deck.title}"!` : decklistText}
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  rows={5}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {deckUrl
                    ? "The link to your deck will be automatically included."
                    : "The decklist will be automatically included if you leave this empty."
                  }
                </p>
              </div>
              <div>
                <label className="text-sm font-medium">Attach up to 4 Card Images</label>
                <ScrollArea className="h-48 mt-1">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 p-1">
                    {scryfallCards.map(card => {
                      if (!card.scryfallCard) return null;
                      const imageUrl = card.scryfallCard.image_uris?.art_crop || card.scryfallCard.image_uris?.large || card.scryfallCard.image_uris?.normal;
                      if (!imageUrl) return null;

                      return (
                        <div
                          key={card.scryfallCard.id}
                          onClick={() => toggleImageSelection(card)}
                          className={cn(
                            "relative rounded-lg overflow-hidden cursor-pointer border-2",
                            selectedImages.includes(imageUrl) ? "border-primary" : "border-transparent"
                          )}
                        >
                          <img src={imageUrl} alt={card.name} className="aspect-[5/7] w-full object-cover" />
                          {selectedImages.includes(imageUrl) && (
                            <div className="absolute inset-0 bg-primary/50 flex items-center justify-center">
                              <Check className="h-8 w-8 text-white" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
              <Button onClick={handlePublishNote} disabled={isPending} className="w-full">
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Publish Note
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
